import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Pregunta, PreguntaCancion, PreguntaPersonaje, Dificultad, Modalidad, PartidaClassic, PartidaNoHit, CheckAnswerResponse, TipoPregunta } from '../types';
import { getApiUrl } from '../config';
import Level1Trivia from '../components/Level1Trivia';
import Level2Portada from '../components/Level2Portada';
import Level3Personaje from '../components/Level3Personaje';
import Level4Cancion from '../components/Level4Cancion';
import AnimatedScore from '../components/AnimatedScore';
import GameFeedbackWidget from '../components/GameFeedbackWidget';
import { playSuccessSound, playPartialSuccessSound, playErrorSound, playLevelUpSound, activateAudio, toggleMute, isMuted } from '../utils/soundEffects';

// Puntos fijos solo para trivia (los demás niveles los calcula el backend)
const PUNTOS_TRIVIA: Record<Dificultad, number> = { normal: 70, dificil: 90 };

interface GameState {
  modalidad: Modalidad;
  dificultad: Dificultad;
  nickname: string;
}

const Game: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as GameState | null;

  const [preguntas, setPreguntas] = useState<Pregunta[]>([]);
  const [indice, setIndice] = useState(0);
  const [puntos, setPuntos] = useState(0);
  const [aciertos, setAciertos] = useState(0);
  const [respuestasPorTipo, setRespuestasPorTipo] = useState<Record<TipoPregunta, number>>({
    trivia: 0,
    portada: 0,
    personaje: 0,
    cancion: 0,
  });
  const [aciertosPorTipo, setAciertosPorTipo] = useState<Record<TipoPregunta, number>>({
    trivia: 0,
    portada: 0,
    personaje: 0,
    cancion: 0,
  });
  const [falloFinalTipo, setFalloFinalTipo] = useState<TipoPregunta | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  // Estado de la pregunta actual
  const [respondida, setRespondida] = useState(false);
  const [respuestaJugador, setRespuestaJugador] = useState('');
  const [respuestaSecundariaJugador, setRespuestaSecundariaJugador] = useState('');
  const [fueCorrecta, setFueCorrecta] = useState<boolean | null>(null);
  const [puntosGanados, setPuntosGanados] = useState(0);
  const [desglose, setDesglose] = useState<CheckAnswerResponse['desglose']>(undefined);

  // Opciones de autocomplete
  const [opcionesJuegos, setOpcionesJuegos] = useState<string[]>([]);
  const [opcionesPersonajes, setOpcionesPersonajes] = useState<string[]>([]);
  const [opcionesCanciones, setOpcionesCanciones] = useState<string[]>([]);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelUpFrom, setLevelUpFrom] = useState<number | null>(null);
  const [levelUpNivel, setLevelUpNivel] = useState<number | null>(null);
  const [isLevelTransitioning, setIsLevelTransitioning] = useState(false);
  const [soundMuted, setSoundMuted] = useState(() => isMuted());

  // Redirigir si no hay state
  useEffect(() => {
    if (!state) {
      navigate('/lobby');
    }
  }, [state, navigate]);

  // Cargar preguntas y opciones de autocomplete
  useEffect(() => {
    if (!state) return;
    const { modalidad, dificultad } = state;

    const cargarTodo = async () => {
      try {
        activateAudio();
        setCargando(true);
        const [dataPartida, dataJuegos, dataPersonajes, dataCanciones] = await Promise.all([
          axios.get(getApiUrl(`/api/game/${modalidad}?dificultad=${dificultad}`)),
          axios.get(getApiUrl('/api/game/autocomplete/juegos')),
          axios.get(getApiUrl('/api/game/autocomplete/personajes')),
          axios.get(getApiUrl('/api/game/autocomplete/canciones')),
        ]);

        // Classic: extraer preguntas de los niveles
        if (modalidad === 'classic') {
          const partida: PartidaClassic = dataPartida.data;
          const todasLasPreguntas: Pregunta[] = [];
          for (const nivel of partida.niveles) {
            todasLasPreguntas.push(...nivel.preguntas);
          }
          setPreguntas(todasLasPreguntas);
        } else {
          const partida: PartidaNoHit = dataPartida.data;
          setPreguntas(partida.preguntas);
        }

        setOpcionesJuegos(dataJuegos.data);
        setOpcionesPersonajes(dataPersonajes.data);
        setOpcionesCanciones(dataCanciones.data);
      } catch (e) {
        setError('Error al cargar el juego. ¿Está el servidor activo?');
      } finally {
        setCargando(false);
      }
    };

    cargarTodo();
  }, [state]);

  const preguntaActual = preguntas[indice];

  const nivelActual = preguntaActual?.nivel ?? 1;
  const totalPreguntasNivelActual = useMemo(() => {
    if (!state || state.modalidad !== 'classic') return preguntas.length;
    return preguntas.filter(p => p.nivel === nivelActual).length;
  }, [preguntas, nivelActual, state]);

  const indiceNivelActual = useMemo(() => {
    if (!state || state.modalidad !== 'classic') return indice + 1;
    return preguntas.slice(0, indice + 1).filter(p => p.nivel === nivelActual).length;
  }, [preguntas, indice, nivelActual, state]);

  const siguientePregunta = preguntas[indice + 1];
  const esUltimaDelNivelActual = !!state
    && state.modalidad === 'classic'
    && !!preguntaActual
    && (!siguientePregunta || siguientePregunta.nivel !== preguntaActual.nivel);

  const handleResponder = useCallback(async (respuestaPrincipal: string, respuestaSecundaria?: string) => {
    if (!state || !preguntaActual || respondida) return;

    let correcto = false;
    let pts = 0;
    let desgloseData: CheckAnswerResponse['desglose'] = undefined;

    try {
      if (preguntaActual.tipo === 'trivia') {
        correcto = respuestaPrincipal === preguntaActual.respuesta;
        pts = correcto ? PUNTOS_TRIVIA[state.dificultad] : 0;
      } else {
        // Construir preguntaData según el tipo
        let preguntaData: Record<string, unknown> = {};

        if (preguntaActual.tipo === 'portada') {
          preguntaData = { titulo: preguntaActual.respuesta, saga: preguntaActual.saga || '' };
        } else if (preguntaActual.tipo === 'personaje') {
          const p = preguntaActual as PreguntaPersonaje;
          preguntaData = {
            nombre: p.respuesta,
            alias: p.alias || '',
            saga: p.saga || '',
            juegosPersonaje: p.juegosPersonaje || [],
          };
        } else if (preguntaActual.tipo === 'cancion') {
          preguntaData = { juegos: (preguntaActual as PreguntaCancion).juegos };
        }

        const { data } = await axios.post<CheckAnswerResponse>(getApiUrl('/api/game/check-answer'), {
          tipo: preguntaActual.tipo,
          dificultad: state.dificultad,
          respuestaPrincipal,
          respuestaSecundaria,
          preguntaData,
        });
        correcto = data.correcta;
        pts = data.puntosGanados;
        desgloseData = data.desglose;
      }
    } catch (e) {
      console.error('Error al validar respuesta:', e);
      setError('No se pudo validar la respuesta. Intenta de nuevo.');
      return;
    }

    setRespuestaJugador(respuestaPrincipal);
    setRespuestaSecundariaJugador(respuestaSecundaria || '');
    setFueCorrecta(correcto);
    setPuntosGanados(pts);
    setDesglose(desgloseData);
    setRespondida(true);
    setRespuestasPorTipo((prev) => ({
      ...prev,
      [preguntaActual.tipo]: (prev[preguntaActual.tipo] || 0) + 1,
    }));
    if (correcto) {
      setAciertosPorTipo((prev) => ({
        ...prev,
        [preguntaActual.tipo]: (prev[preguntaActual.tipo] || 0) + 1,
      }));
    } else if (state.modalidad === 'nohit') {
      setFalloFinalTipo(preguntaActual.tipo);
    }

    // Reproducir sonido según resultado
    if (preguntaActual.tipo === 'trivia' || preguntaActual.tipo === 'cancion') {
      // Normal y difícil cancion: solo correcto/incorrecto
      if (correcto) playSuccessSound(); else playErrorSound();
    } else if (preguntaActual.tipo === 'portada') {
      if (state.dificultad === 'dificil' && desgloseData) {
        if (desgloseData.tituloCorrect && desgloseData.sagaCorrect) playSuccessSound();
        else if (desgloseData.sagaCorrect) playPartialSuccessSound();
        else playErrorSound();
      } else {
        if (correcto) playSuccessSound(); else playErrorSound();
      }
    } else if (preguntaActual.tipo === 'personaje') {
      if (state.dificultad === 'dificil' && desgloseData) {
        if (desgloseData.tituloCorrect) playSuccessSound();
        else if (desgloseData.tituloMismaSaga) playPartialSuccessSound();
        else playErrorSound();
      } else {
        if (correcto) playSuccessSound(); else playErrorSound();
      }
    }

    if (pts > 0) {
      setPuntos(prev => prev + pts);
      setAciertos(prev => prev + 1);
    }

  }, [state, preguntaActual, respondida]);

  const handleContinuar = useCallback(() => {
    if (!state || isLevelTransitioning || !preguntaActual) return;

    // No Hit: si no ganó puntos, terminar partida
    if (state.modalidad === 'nohit' && puntosGanados === 0) {
      navigate('/results', {
        state: {
          nickname: state.nickname,
          puntos,
          aciertos,
          totalPreguntas: indice + 1,
          dificultad: state.dificultad,
          modalidad: state.modalidad,
          respuestasPorTipo,
          aciertosPorTipo,
          falloFinalTipo: preguntaActual.tipo,
        }
      });
      return;
    }

    const siguienteIndice = indice + 1;
    if (siguienteIndice >= preguntas.length) {
      // Fin del juego
      navigate('/results', {
        state: {
          nickname: state.nickname,
          puntos,
          aciertos,
          totalPreguntas: preguntas.length,
          dificultad: state.dificultad,
          modalidad: state.modalidad,
          respuestasPorTipo,
          aciertosPorTipo,
          falloFinalTipo: state.modalidad === 'nohit' ? falloFinalTipo : null,
        }
      });
    } else {
      const siguientePregunta = preguntas[siguienteIndice];
      const hayCambioNivel = state.modalidad === 'classic' && siguientePregunta && siguientePregunta.nivel !== preguntaActual.nivel;

      if (hayCambioNivel) {
        setLevelUpFrom(preguntaActual.nivel);
        setLevelUpNivel(siguientePregunta.nivel);
        setShowLevelUp(true);
        setIsLevelTransitioning(true);
        playLevelUpSound();

        setTimeout(() => {
          setIndice(siguienteIndice);
          setRespondida(false);
          setRespuestaJugador('');
          setRespuestaSecundariaJugador('');
          setFueCorrecta(null);
          setPuntosGanados(0);
          setDesglose(undefined);
          setShowLevelUp(false);
          setIsLevelTransitioning(false);
        }, 1500);
        return;
      }

      // Siguiente pregunta
      setIndice(siguienteIndice);
      setRespondida(false);
      setRespuestaJugador('');
      setRespuestaSecundariaJugador('');
      setFueCorrecta(null);
      setPuntosGanados(0);
      setDesglose(undefined);
    }
  }, [state, isLevelTransitioning, preguntaActual, puntosGanados, puntos, aciertos, indice, preguntas, navigate, respuestasPorTipo, aciertosPorTipo, falloFinalTipo]);

  // ─── Pantalla de carga ──────────────────────────────────────────────────
  if (cargando) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gaming-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-gaming text-white text-sm">CARGANDO PARTIDA...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card-gaming text-center max-w-md">
          <p className="text-gaming-red font-gaming text-sm mb-4">ERROR</p>
          <p className="font-main text-white/70 mb-6">{error}</p>
          <button onClick={() => navigate('/lobby')} className="btn-primary">Volver al lobby</button>
        </div>
      </div>
    );
  }

  if (!preguntaActual || !state) return null;

  // ─── Header del juego ───────────────────────────────────────────────────
  const preguntasCompletadasNivel = state.modalidad === 'classic'
    ? Math.max(0, indiceNivelActual - (respondida ? 0 : 1))
    : 0;

  const porcentajeProgreso = state.modalidad === 'classic'
    ? (totalPreguntasNivelActual > 0 ? (preguntasCompletadasNivel / totalPreguntasNivelActual) * 100 : 0)
    : 0;

  const getNombreNivel = (nivel: number | null) => {
    if (nivel === 2) return 'PORTADAS';
    if (nivel === 3) return 'PERSONAJES';
    if (nivel === 4) return 'CANCIONES';
    return 'PREGUNTAS';
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Barra superior */}
      <header className="border-b border-white/10 px-4 py-2 md:py-4 md:px-6 flex flex-col gap-1.5 md:gap-2">
        <div className="max-w-5xl mx-auto w-full">
          {/* Desktop header layout */}
          <div className="hidden md:flex w-full items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="font-main text-white/60 text-base">{state.nickname}</span>
              <span className={`badge-dificultad ${state.dificultad === 'dificil' ? 'badge-dificil' : 'badge-normal'}`}>
                {state.dificultad === 'dificil' ? 'Difícil' : 'Normal'}
              </span>
              <button
                onClick={() => { const m = toggleMute(); setSoundMuted(m); }}
                className="font-gaming text-2xl leading-none text-white/70 hover:text-white transition-colors px-2 py-1"
                title={soundMuted ? 'Activar sonidos' : 'Silenciar sonidos'}
              >
                {soundMuted ? '🔇' : '🔊'}
              </button>
              <button
                onClick={() => navigate('/')}
                className="font-gaming text-xs tracking-wider px-3 py-2 rounded-lg border border-red-400/50 text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-colors"
                title="Salir de la partida"
              >
                SALIR
              </button>
            </div>
            
            {/* Desktop stats */}
            <div className="flex items-center gap-6">
              {state.modalidad === 'classic' ? (
                <>
                  <div className="game-stat-chip text-right">
                    <p className="font-main text-white/50 text-xs tracking-wider">PUNTOS</p>
                    <AnimatedScore currentScore={puntos} isStreamInterface />
                  </div>
                  <div className="game-stat-chip text-right">
                    <p className="font-main text-white/50 text-xs tracking-wider">{`NIVEL ${nivelActual}`}</p>
                    <p className="font-gaming text-white text-xl leading-none">
                      {`${indiceNivelActual} / ${totalPreguntasNivelActual}`}
                    </p>
                  </div>
                </>
              ) : (
                <div className="game-stat-chip text-right">
                  <p className="font-main text-white/50 text-xs tracking-wider">RACHA</p>
                  <p className="font-gaming text-white text-xl leading-none">{aciertos}</p>
                </div>
              )}
            </div>
          </div>

          {/* Mobile header layout */}
          <div className="flex md:hidden w-full items-center justify-between gap-2">
            {/* Left: Mute */}
            <div className="flex-1 flex justify-start">
              <button
                onClick={() => { const m = toggleMute(); setSoundMuted(m); }}
                className="font-gaming text-xl leading-none text-white/70 hover:text-white transition-colors px-1 py-1"
                title={soundMuted ? 'Activar sonidos' : 'Silenciar sonidos'}
              >
                {soundMuted ? '🔇' : '🔊'}
              </button>
            </div>

            {/* Center: Nickname • Difficulty */}
            <div className="flex items-center justify-center gap-2 font-main text-sm text-white/60">
              <span className="text-white/80 font-medium">{state.nickname}</span>
              <span>•</span>
              <span className={`badge-dificultad text-[11px] px-1.5 py-0.5 leading-normal ${state.dificultad === 'dificil' ? 'badge-dificil' : 'badge-normal'}`}>
                {state.dificultad === 'dificil' ? 'Difícil' : 'Normal'}
              </span>
            </div>

            {/* Right: Salir */}
            <div className="flex-1 flex justify-end">
              <button
                onClick={() => navigate('/')}
                className="font-gaming text-[11px] tracking-wider px-2.5 py-1.5 rounded-lg border border-red-400/50 text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-colors"
                title="Salir de la partida"
              >
                SALIR
              </button>
            </div>
          </div>
        </div>
        
        {/* Barra de progreso (solo Classic) */}
        {state.modalidad === 'classic' && (
          <div className="max-w-5xl mx-auto w-full mt-1">
            <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-gaming-neon-green to-gaming-neon-orange rounded-full transition-all duration-500"
                style={{ width: `${porcentajeProgreso}%` }}
              />
            </div>
          </div>
        )}

        {/* Mobile stats */}
        <div className="flex md:hidden items-center justify-center gap-4 mt-1">
          {state.modalidad === 'classic' ? (
            <>
              <div className="game-stat-chip text-right flex items-center justify-between gap-4 py-1.5 px-3 min-w-[110px]">
                <p className="font-main text-white/50 text-xs tracking-wider">PUNTOS</p>
                <AnimatedScore currentScore={puntos} isStreamInterface />
              </div>
              <div className="game-stat-chip text-right flex items-center justify-between gap-4 py-1.5 px-3 min-w-[130px]">
                <p className="font-main text-white/50 text-xs tracking-wider">{`NIVEL ${nivelActual}`}</p>
                <p className="font-gaming text-white text-base leading-none">
                  {`${indiceNivelActual} / ${totalPreguntasNivelActual}`}
                </p>
              </div>
            </>
          ) : (
            <div className="game-stat-chip text-right flex items-center justify-between gap-4 py-1.5 px-3 min-w-[110px]">
              <p className="font-main text-white/50 text-xs tracking-wider">RACHA</p>
              <p className="font-gaming text-white text-base leading-none">{aciertos}</p>
            </div>
          )}
        </div>
      </header>

      {/* Contenido principal */}
      <main className="relative flex-1">
        {showLevelUp && levelUpNivel !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-transparent">
            <div className="level-up-card px-8 py-6 rounded-2xl border-2 border-gaming-neon-green bg-black/80 shadow-[0_0_40px_rgba(0,255,136,0.45)]">
              <p className="font-main text-white/70 text-sm tracking-[0.25em] text-center mb-2">LEVEL UP</p>
              <div className="level-up-arrows mb-2">
                <span>↑</span>
                <span>↑</span>
                <span>↑</span>
              </div>
              <p className="font-gaming text-gaming-neon-green text-2xl md:text-4xl tracking-wider text-center">
                NIVEL {levelUpFrom ?? Math.max(1, levelUpNivel - 1)} {'->'} {levelUpNivel}
              </p>
              <p className="font-main text-gaming-cyan text-3xl md:text-4xl tracking-[0.2em] text-center mt-2">
                {getNombreNivel(levelUpNivel)}
              </p>
            </div>
          </div>
        )}

        <div className={`max-w-4xl mx-auto px-4 md:px-6 pt-1.5 pb-3 md:py-10 ${showLevelUp ? 'opacity-0 pointer-events-none select-none' : ''}`}>
          <div className="relative">
            {/* Feedback o pregunta */}
            <div className="question-stage-shell mt-1 md:mt-0">
            <>
              {preguntaActual.tipo === 'trivia' && (
                <Level1Trivia
                  pregunta={preguntaActual}
                  onResponder={handleResponder}
                  respondida={respondida}
                  respuestaJugador={respuestaJugador}
                  fueCorrecta={fueCorrecta}
                  onContinuar={handleContinuar}
                  esUltima={indice + 1 >= preguntas.length}
                  esUltimaDelNivel={esUltimaDelNivelActual}
                  modalidad={state.modalidad}
                  puntosGanados={puntosGanados}
                />
              )}
              {preguntaActual.tipo === 'portada' && (
                <Level2Portada
                  pregunta={preguntaActual}
                  onResponder={handleResponder}
                  respondida={respondida}
                  fueCorrecta={fueCorrecta}
                  opciones={opcionesJuegos}
                  dificultad={state.dificultad}
                  desglose={desglose}
                  onContinuar={handleContinuar}
                  esUltima={indice + 1 >= preguntas.length}
                  esUltimaDelNivel={esUltimaDelNivelActual}
                  modalidad={state.modalidad}
                  puntosGanados={puntosGanados}
                />
              )}
              {preguntaActual.tipo === 'personaje' && (
                <Level3Personaje
                  pregunta={preguntaActual}
                  onResponder={handleResponder}
                  respondida={respondida}
                  fueCorrecta={fueCorrecta}
                  opciones={opcionesPersonajes}
                  opcionesJuegos={opcionesJuegos}
                  dificultad={state.dificultad}
                  desglose={desglose}
                  respuestaJugador={respuestaJugador}
                  respuestaSecundariaJugador={respuestaSecundariaJugador}
                  onContinuar={handleContinuar}
                  esUltima={indice + 1 >= preguntas.length}
                  esUltimaDelNivel={esUltimaDelNivelActual}
                  modalidad={state.modalidad}
                  puntosGanados={puntosGanados}
                />
              )}
              {preguntaActual.tipo === 'cancion' && (
                <Level4Cancion
                  pregunta={preguntaActual}
                  onResponder={handleResponder}
                  respondida={respondida}
                  fueCorrecta={fueCorrecta}
                  opciones={opcionesJuegos}
                  dificultad={state.dificultad}
                  desglose={desglose}
                  respuestaJugador={respuestaJugador}
                  onContinuar={handleContinuar}
                  esUltima={indice + 1 >= preguntas.length}
                  modalidad={state.modalidad}
                  puntosGanados={puntosGanados}
                />
              )}
            </>
            </div>

            <GameFeedbackWidget
              nickname={state.nickname}
              modalidad={state.modalidad}
              dificultad={state.dificultad}
              nivelActual={nivelActual}
              preguntaActual={preguntaActual}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Game;
